import * as React from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import type { EventApi, EventClickArg, EventContentArg, EventInput } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";

import {
  getExternalEvents,
  getMyBookings,
  getMyRollingAppointments,
  listBlackouts,
} from "@/api/client";
import {
  BlackoutView,
  BookingSlotView,
  ExternalEventView,
  RollingAppointmentSlotView,
} from "@/api/types";
import { calendarProviderName } from "@/lib/calendarProviders";
import { CalendarZoom } from "@/components/CalendarZoom";
import { ComicPanel } from "@/components/comic";
import { Switch } from "@/components/ui/switch";
import { glideCalendar } from "@/lib/calendarGlide";
import { cn } from "@/lib/utils";

import { CancelBookingPopover, CancelBookingTarget } from "../booking/CancelBookingPopover";
import { bookingLinkFor } from "../booking/links";
import { MeetingListRow } from "./MeetingListRow";
import {
  icalStatus,
  isLiveNegotiation,
  meetingTitle,
  ReschedulePick,
  UiMeeting,
} from "./shared";
import "./fullcalendar-theme.css";

/** Row height (rem) per 30-min slot, controlling how much of the day is visible. */
const DEFAULT_SLOT_H = 1.5; // ~12h in the collapsed panel; lands scrolled to the working day
const EXPANDED_SLOT_H = 0.85; // compresses the whole day into the expanded panel
const MIN_SLOT_H = 0.5;
const MAX_SLOT_H = 3.5;
const SLOT_H_STEP = 0.12;

/** Persist the user's preferred (collapsed) hour zoom so the calendar reopens at it. */
const SLOT_H_KEY = "scheda.meetings.slotH";
function readStoredSlotH(): number | null {
  try {
    const v = Number(window.localStorage.getItem(SLOT_H_KEY));
    return Number.isFinite(v) && v >= MIN_SLOT_H && v <= MAX_SLOT_H ? v : null;
  } catch {
    return null;
  }
}
function writeStoredSlotH(v: number): void {
  try {
    window.localStorage.setItem(SLOT_H_KEY, String(v));
  } catch {
    /* storage disabled/full — the zoom just won't persist */
  }
}

/**
 * Block content: the title, and in the timed views who it's with — but never
 * the start time. The block's own position on the grid already states when it
 * is, so a "09:00 – 09:30" label inside a cell that literally sits at 09:00 is
 * noise. (Background/blackout bands keep their default rendering.)
 */
function renderEventContent(arg: EventContentArg) {
  if (arg.event.display === "background") return undefined;
  const external = arg.event.extendedProps.external as boolean | undefined;
  const booking = arg.event.extendedProps.booking as boolean | undefined;
  const inviteeName = arg.event.extendedProps.inviteeName as string | null | undefined;
  const participants = arg.event.extendedProps.participants as string | undefined;
  if (arg.view.type === "dayGridMonth") {
    return <div className="truncate px-1 font-medium">{arg.event.title}</div>;
  }
  const sublabel = external
    ? `from ${calendarProviderName(arg.event.extendedProps.provider as string, "short")}`
    : booking
      ? inviteeName
        ? `booked · ${inviteeName}`
        : "booked"
      : participants;
  return (
    <div className="overflow-hidden px-1 leading-tight">
      <div className="truncate font-semibold">{arg.event.title}</div>
      {sublabel ? (
        <div className="truncate text-[10px] opacity-70">{sublabel}</div>
      ) : null}
    </div>
  );
}

/** Full detail on hover — a compact block can't hold everyone's name. */
function setEventTooltip(arg: EventContentArg & { el: HTMLElement }) {
  if (arg.event.display === "background") return;
  if (arg.event.extendedProps.external) {
    const from = calendarProviderName(arg.event.extendedProps.provider as string);
    arg.el.title = `From ${from}\n${arg.event.title}`;
    return;
  }
  if (arg.event.extendedProps.booking) {
    const name = arg.event.extendedProps.inviteeName as string | null | undefined;
    arg.el.title = `Booking · ${arg.event.title}${name ? `\nBooked by: ${name}` : ""}`;
    return;
  }
  const p = arg.event.extendedProps.participants as string | undefined;
  arg.el.title = p ? `${arg.event.title}\nWith: ${p}` : arg.event.title;
}

/**
 * Faint shading on alternate two-hour bands (00–02, 04–06, …) so the eye can
 * track roughly where in the day a block sits without counting hour lines.
 */
function bandClass(arg: { date?: Date }): string {
  const h = arg.date?.getHours() ?? 0;
  return Math.floor(h / 2) % 2 === 0 ? "fc-band-shade" : "";
}

/**
 * The week/month calendar, built on FullCalendar.
 *
 * <p>This is the foundation the richer interactions (drag-to-reschedule,
 * marquee availability selection, density) build on in later increments — so it
 * is deliberately read-only: {@code editable} and {@code selectable} are off.
 * What it does deliver: a week and a month view you switch between from the
 * toolbar, the whole active day fit on screen without an inner scroll, and each
 * block painted by its iCalendar status — tentative (hatched), confirmed
 * (solid), cancelled (faded/struck) — via {@link icalStatus}.</p>
 *
 * <p>Only meetings with a decided slot land on the grid; still-live
 * negotiations with no time yet drop into a strip below. A deep link
 * (?meeting=&lt;id&gt;) jumps the calendar to that meeting's date and selects
 * it; selecting a block opens its full row beneath the calendar.</p>
 */
export function MeetingsCalendarView({
  meetings,
  now,
  focusMeetingId,
  onCancel,
  onReschedule,
  onAbandonReschedule,
  onOpenNegotiation,
  onBookingCancelled,
  onBookingCancelFailed,
}: {
  meetings: UiMeeting[];
  now: Date;
  /** Deep-linked meeting: jump to its week, select it. */
  focusMeetingId?: string | null;
  onCancel?: (id: string) => void;
  onReschedule?: (id: string, pick: ReschedulePick) => void;
  onAbandonReschedule?: (id: string) => void;
  onOpenNegotiation?: (id: string) => void;
  /** The host cancelled a booked block from the grid. The page raises the toast. */
  onBookingCancelled?: (invitee: string) => void;
  /** A cancel attempt failed, so the page can say so even after the panel closes. */
  onBookingCancelFailed?: (message: string) => void;
}) {
  const calendarRef = React.useRef<FullCalendar | null>(null);
  const boxRef = React.useRef<HTMLDivElement | null>(null);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  // Give the grid more room on demand — a taller canvas that fits the whole day —
  // without leaving the page.
  const [expanded, setExpanded] = React.useState(false);
  // How tall each 30-min row is (rem). Ctrl+scroll zooms it; Expand compresses it so
  // the full day fits. The whole day is always in range and scrollable either way.
  // Seeds from the user's last collapsed zoom (localStorage).
  const [slotH, setSlotH] = React.useState<number>(() => readStoredSlotH() ?? DEFAULT_SLOT_H);

  // Remember the collapsed zoom, but not the transient Expand height.
  React.useEffect(() => {
    if (!expanded) writeStoredSlotH(slotH);
  }, [slotH, expanded]);

  // Imported external-calendar events for the currently-visible range, fetched on every
  // view/date change (datesSet). Empty when the user hasn't imported — the
  // endpoint just returns nothing, so this is safe regardless of connection.
  const [externalEvents, setExternalEvents] = React.useState<ExternalEventView[]>([]);
  const loadExternal = React.useCallback((from: Date, to: Date) => {
    getExternalEvents(from.toISOString(), to.toISOString())
      .then(setExternalEvents)
      .catch(() => {
        /* leave the last set in place — a transient failure shouldn't blank the grid */
      });
  }, []);

  const navigate = useNavigate();
  // Confirmed booking-page bookings show on the calendar as first-class entries (a booked slot
  // belongs here even though it wasn't A2A-negotiated). On by default; the toggle can hide them.
  // Only fetched for the visible range while shown.
  const [showBookings, setShowBookings] = React.useState(true);
  const [bookings, setBookings] = React.useState<BookingSlotView[]>([]);
  const [rollingAppointments, setRollingAppointments] = React.useState<RollingAppointmentSlotView[]>(
    [],
  );
  const [bookingRange, setBookingRange] = React.useState<{ from: string; to: string } | null>(null);
  // Bumped after a host cancel so the block disappears. Both endpoints are CONFIRMED-only, so a
  // cancelled booking simply drops out of the next answer with nothing else to reconcile.
  const [bookingsVersion, setBookingsVersion] = React.useState(0);
  React.useEffect(() => {
    if (!showBookings || !bookingRange) {
      setBookings([]);
      setRollingAppointments([]);
      return;
    }
    let cancelled = false;
    getMyBookings(bookingRange.from, bookingRange.to)
      .then((b) => {
        if (!cancelled) setBookings(b);
      })
      .catch(() => {
        /* transient failure — keep the last set rather than blank the grid */
      });
    getMyRollingAppointments(bookingRange.from, bookingRange.to)
      .then((a) => {
        if (!cancelled) setRollingAppointments(a);
      })
      .catch(() => {
        /* same: a rolling blip must not blank the ad-hoc blocks beside it */
      });
    return () => {
      cancelled = true;
    };
  }, [showBookings, bookingRange, bookingsVersion]);

  // Only meetings with a real decided slot get a place on the grid; the state
  // still decides how each one is painted (icalStatus). Which STATES arrive
  // here is the caller's filter chips' business — the default view already
  // hides cancelled/superseded rows, so this stays "the real schedule" without
  // re-filtering by state.
  const slotted = React.useMemo(
    () => meetings.filter((m) => m.start !== null && m.end !== null),
    [meetings],
  );
  // Below the grid: still-live negotiations with no decided slot yet.
  const unslotted = React.useMemo(
    () => meetings.filter((m) => m.start === null && isLiveNegotiation(m.state)),
    [meetings],
  );

  // The times the SELECTED meeting's chain has already ruled out for this user —
  // fetched only for the selection ("why not 14:00?" for the one being looked
  // at), drawn as hatched background bands.
  const [blackouts, setBlackouts] = React.useState<BlackoutView[]>([]);
  React.useEffect(() => {
    if (!selectedId) {
      setBlackouts([]);
      return;
    }
    let cancelled = false;
    listBlackouts(selectedId)
      .then((b) => {
        if (!cancelled) setBlackouts(b);
      })
      .catch(() => {
        if (!cancelled) setBlackouts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const zoomBy = React.useCallback((delta: number) => {
    setSlotH((h) => Math.max(MIN_SLOT_H, Math.min(MAX_SLOT_H, Number((h + delta).toFixed(2)))));
  }, []);

  // Ctrl + mouse-wheel zooms the hour rows: scroll down flattens them so more of the
  // day comes into the panel, scroll up makes them taller. Capture phase + non-passive
  // so we intercept before FullCalendar's own scroller and can preventDefault. (On some
  // machines — notably macOS with the accessibility zoom gesture on — the OS eats Ctrl+
  // scroll before the browser sees it; the ± buttons are the reliable fallback.)
  React.useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      zoomBy(e.deltaY > 0 ? -SLOT_H_STEP : SLOT_H_STEP);
    };
    el.addEventListener("wheel", onWheel, { passive: false, capture: true });
    return () => el.removeEventListener("wheel", onWheel, { capture: true } as EventListenerOptions);
  }, [zoomBy]);

  // FullCalendar needs a nudge to re-measure after the row height changes.
  React.useEffect(() => {
    calendarRef.current?.getApi().updateSize();
  }, [slotH]);

  const glide = React.useCallback((dir: "next" | "prev") => {
    const api = calendarRef.current?.getApi();
    if (api) glideCalendar(api, boxRef.current, dir);
  }, []);

  // The grid spans the whole day (so zoom reveals any hour), but land near where the
  // meetings actually sit: the earliest meeting's hour, or 08:00 by default.
  const scrollTime = React.useMemo(() => {
    let h = 8;
    for (const m of slotted) {
      if (m.start) h = Math.min(h, m.start.getHours());
    }
    return `${String(Math.max(0, h)).padStart(2, "0")}:00:00`;
  }, [slotted]);

  const events = React.useMemo<EventInput[]>(() => {
    const meetingEvents: EventInput[] = slotted.map((m) => ({
      id: m.id,
      title: meetingTitle(m),
      start: m.start!,
      end: m.end ?? undefined,
      classNames: [
        `fc-status-${icalStatus(m.state).toLowerCase()}`,
        m.id === selectedId ? "fc-selected" : "",
      ].filter(Boolean),
      extendedProps: {
        participants: m.participants.join(", "),
      },
    }));
    const externalEventInputs: EventInput[] = externalEvents.map((e) => ({
      id: `ext-${e.id}`,
      title: e.title,
      start: e.start,
      end: e.end,
      allDay: e.allDay,
      editable: false,
      classNames: ["fc-status-external"],
      extendedProps: { external: true, provider: e.provider },
    }));
    const bookingEvents: EventInput[] = bookings.map((b) => ({
      id: `booking-${b.id}`,
      title: b.title,
      start: b.start,
      end: b.end,
      editable: false,
      // Clickable: opens the cancel panel, which also carries the link into the booking view.
      classNames: ["fc-status-booking", "cursor-pointer"],
      extendedProps: {
        booking: true,
        kind: "adhoc" as const,
        inviteeName: b.inviteeName,
        bookingId: b.id,
        linkId: b.bookingPageId,
        bookingPageId: b.bookingPageId,
        slotStart: b.start,
      },
    }));
    const rollingEvents: EventInput[] = rollingAppointments.map((a) => ({
      id: `rolling-${a.id}`,
      title: a.title,
      start: a.start,
      end: a.end,
      editable: false,
      classNames: ["fc-status-rolling", "cursor-pointer"],
      extendedProps: {
        booking: true,
        kind: "rolling" as const,
        inviteeName: a.inviteeName,
        bookingId: a.id,
        linkId: a.rollingLinkId,
        slotStart: a.start,
      },
    }));
    const blackoutEvents: EventInput[] = blackouts.map((b, i) => ({
      id: `blackout-${i}`,
      start: b.start,
      end: b.end,
      display: "background" as const,
      classNames: ["fc-blackout"],
    }));
    return [
      ...meetingEvents,
      ...externalEventInputs,
      ...bookingEvents,
      ...rollingEvents,
      ...blackoutEvents,
    ];
  }, [slotted, selectedId, blackouts, externalEvents, bookings, rollingAppointments]);

  // A deep link may land on a meeting weeks away, so move the calendar to it and
  // select it. Keyed on the id + its start time (not the object) so a 10s poll
  // refresh doesn't keep yanking the view back.
  const focused = focusMeetingId
    ? (slotted.find((m) => m.id === focusMeetingId) ?? null)
    : null;
  const focusedStart = focused?.start?.getTime() ?? null;
  React.useEffect(() => {
    if (!focused || !focused.start) return;
    calendarRef.current?.getApi().gotoDate(focused.start);
    setSelectedId(focused.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on id + start
  }, [focusMeetingId, focusedStart]);

  // The container height animates on expand/collapse; FullCalendar only re-fits
  // its rows on a window resize, so tell it to re-measure once the transition ends.
  React.useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    const t = window.setTimeout(() => api.updateSize(), 320);
    return () => window.clearTimeout(t);
  }, [expanded]);

  // Which booked block the cancel panel is open on, and the element it hangs off.
  const [cancelTarget, setCancelTarget] = React.useState<{
    target: CancelBookingTarget;
    anchor: HTMLElement;
  } | null>(null);

  /** Builds a cancel target from a booked block, or null if this event is not one. */
  const targetFor = React.useCallback((event: EventApi): CancelBookingTarget | null => {
    const props = event.extendedProps as {
      booking?: boolean;
      kind?: "adhoc" | "rolling";
      bookingId?: string;
      linkId?: string;
      inviteeName?: string | null;
    };
    if (!props.booking || !props.kind || !props.bookingId || !props.linkId) return null;
    return {
      kind: props.kind,
      linkId: props.linkId,
      bookingId: props.bookingId,
      invitee: props.inviteeName || "this invitee",
      start: event.start?.toISOString() ?? "",
      end: event.end?.toISOString() ?? null,
    };
  }, []);

  const handleEventClick = React.useCallback(
    (arg: EventClickArg) => {
      // A booked block opens the cancel panel rather than navigating away. The navigation is not
      // lost: it moves inside the panel as "Open in bookings". A calendar grid is dense, and
      // teleporting the host off the page on a stray click is the worse of the two failure modes.
      const target = targetFor(arg.event);
      if (target) {
        setCancelTarget({ target, anchor: arg.el });
        return;
      }
      // Blackout bands and other read-only events (calendar imports) aren't selectable.
      if (arg.event.id.startsWith("blackout-") || arg.event.id.startsWith("ext-")) return;
      setSelectedId(arg.event.id);
    },
    [targetFor],
  );

  // FullCalendar has no context-menu hook, so the listener is attached per event element and
  // removed in the paired eventWillUnmount. Without the pairing this leaks on every re-render,
  // and this grid re-renders often (poll refresh, view change, expand/collapse).
  const contextHandlers = React.useRef(new Map<HTMLElement, (e: MouseEvent) => void>());
  const bindContextMenu = React.useCallback(
    (el: HTMLElement, event: EventApi) => {
      if (!targetFor(event)) return;
      const onMenu = (e: MouseEvent) => {
        e.preventDefault();
        const target = targetFor(event);
        if (target) setCancelTarget({ target, anchor: el });
      };
      el.addEventListener("contextmenu", onMenu);
      contextHandlers.current.set(el, onMenu);
    },
    [targetFor],
  );
  const unbindContextMenu = React.useCallback((el: HTMLElement) => {
    const handler = contextHandlers.current.get(el);
    if (handler) {
      el.removeEventListener("contextmenu", handler);
      contextHandlers.current.delete(el);
    }
  }, []);

  // FullCalendar destroys and rebuilds event elements on a view change, a resize, expand/collapse
  // and every refetch. Anything left open would be positioned against a detached node, so close it.
  React.useEffect(() => {
    setCancelTarget(null);
  }, [expanded, bookingRange, bookings, rollingAppointments]);

  const selected = meetings.find((m) => m.id === selectedId) ?? null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
        <label
          className="flex cursor-pointer items-center gap-2 font-medium text-muted-foreground"
          title="Overlay bookings taken on your public booking pages, read-only."
        >
          <Switch checked={showBookings} onCheckedChange={setShowBookings} />
          Show booking-page bookings
        </label>
        <div className="flex items-center gap-3">
          <CalendarZoom
            onZoomOut={() => zoomBy(-2 * SLOT_H_STEP)}
            onZoomIn={() => zoomBy(2 * SLOT_H_STEP)}
            atMin={slotH <= MIN_SLOT_H}
            atMax={slotH >= MAX_SLOT_H}
          />
          <button
            type="button"
            onClick={() =>
              setExpanded((v) => {
                const next = !v;
                // Expand fits the whole day; collapsing restores the user's saved zoom.
                setSlotH(next ? EXPANDED_SLOT_H : (readStoredSlotH() ?? DEFAULT_SLOT_H));
                return next;
              })
            }
            title={expanded ? "Back to the working-day height" : "Fit the whole day in view"}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 font-medium text-muted-foreground shadow-sm transition hover:bg-muted hover:text-foreground"
          >
            {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
            {expanded ? "Collapse" : "Expand"}
          </button>
        </div>
      </div>
      <div
        ref={boxRef}
        style={{ "--scheda-slot-h": `${slotH}rem` } as React.CSSProperties}
        className={cn(
          "scheda-calendar rounded-3xl border border-border bg-card p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_20px_50px_-30px_rgba(15,23,42,0.28)] transition-[height] duration-300 dark:bg-background",
          expanded ? "h-[calc(100vh-7rem)]" : "h-[72vh] min-h-[440px]",
        )}
      >
        <FullCalendar
          ref={calendarRef}
          plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          initialDate={now}
          views={{
            // A rolling 7 days starting today — parity with the bookings calendar.
            timeGridRolling: {
              type: "timeGrid",
              duration: { days: 7 },
              dateAlignment: "day",
              // Step a full week on prev/next (FullCalendar otherwise defaults to one day).
              dateIncrement: { days: 7 },
              buttonText: "From today",
            },
          }}
          customButtons={{
            // Replace the native arrows so a prev/next glides the 7 days across.
            glidePrev: { icon: "chevron-left", click: () => glide("prev") },
            glideNext: { icon: "chevron-right", click: () => glide("next") },
          }}
          headerToolbar={{
            left: "glidePrev,glideNext today",
            center: "title",
            right: "dayGridMonth,timeGridRolling,timeGridWeek,timeGridDay",
          }}
          buttonText={{
            today: "Today",
            day: "Day",
            week: "Mon–Sun",
            month: "Month",
          }}
          firstDay={1}
          allDaySlot
          nowIndicator
          // Click a day (its number, or an empty month cell) to drop into that day's view;
          // the thin › by each week jumps to that week. navLinks powers both.
          navLinks
          weekNumbers
          navLinkDayClick="timeGridDay"
          navLinkWeekClick="timeGridWeek"
          weekNumberContent={() => "›"}
          dateClick={(arg) => {
            if (arg.view.type === "dayGridMonth") {
              arg.view.calendar.changeView("timeGridDay", arg.date);
            }
          }}
          height="100%"
          scrollTime={scrollTime}
          slotMinTime="00:00:00"
          slotMaxTime="24:00:00"
          slotDuration="00:30:00"
          slotLabelFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
          eventTimeFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
          dayMaxEvents={3}
          events={events}
          datesSet={(a) => {
            loadExternal(a.start, a.end);
            setBookingRange({ from: a.start.toISOString(), to: a.end.toISOString() });
          }}
          eventClick={handleEventClick}
          eventContent={renderEventContent}
          eventDidMount={(arg) => {
            setEventTooltip(arg);
            bindContextMenu(arg.el, arg.event);
          }}
          eventWillUnmount={(arg) => unbindContextMenu(arg.el)}
          displayEventTime={false}
          slotLaneClassNames={bandClass}
          // Read-only for now — drag-to-reschedule and marquee selection land in
          // later increments.
          editable={false}
          selectable={false}
        />
      </div>

      <CancelBookingPopover
        target={cancelTarget?.target ?? null}
        anchor={cancelTarget?.anchor ?? null}
        onClose={() => setCancelTarget(null)}
        onCancelled={(t) => {
          setBookingsVersion((v) => v + 1);
          onBookingCancelled?.(t.invitee);
        }}
        onFailed={(message) => onBookingCancelFailed?.(message)}
        extraAction={
          cancelTarget?.target.kind === "adhoc" ? (
            <button
              type="button"
              onClick={() => {
                const t = cancelTarget.target;
                setCancelTarget(null);
                // Carry the origin so the booking view offers a "← Back to Meetings" (see BackBar).
                navigate(bookingLinkFor(t.linkId, t.start), {
                  state: { from: { label: "Meetings" } },
                });
              }}
              className="rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground transition hover:text-foreground"
            >
              Open in bookings
            </button>
          ) : undefined
        }
      />

      {selected ? (
        <MeetingListRow
          meeting={selected}
          onCancel={onCancel}
          onReschedule={onReschedule}
          onAbandonReschedule={onAbandonReschedule}
          onOpenNegotiation={onOpenNegotiation}
        />
      ) : (
        <p className="text-xs italic text-muted-foreground">Click a block for details.</p>
      )}

      {unslotted.length > 0 && (
        <ComicPanel title="Still negotiating" dense>
          <p className="mb-2 text-[10px] uppercase tracking-wider text-muted-foreground">
            No decided slot yet. Won't appear on the grid until accepted.
          </p>
          <div className="space-y-2">
            {unslotted.map((m) => (
              <MeetingListRow
                key={m.id}
                meeting={m}
                onCancel={onCancel}
                onReschedule={onReschedule}
                onOpenNegotiation={onOpenNegotiation}
              />
            ))}
          </div>
        </ComicPanel>
      )}
    </div>
  );
}
